// ============================================================
// TILT — NPC  (the ghost in the other cabinet)
// ------------------------------------------------------------
// HARVESTED FROM: Terror's NPC.js — the TEMPERAMENT + SKILL-BAND
// architecture. Not the code (a card AI and a pinball AI share
// nothing), but the SHAPE, which is genuinely reusable:
//
//   skill       → HOW WELL it does the thing
//   temperament → WHETHER IT WANTS TO do the thing
//
// Those are orthogonal, and keeping them orthogonal is what
// makes an opponent feel like a PERSON instead of a difficulty
// slider. A high-skill/low-aggression opponent is a wall. A
// low-skill/high-aggression one is a menace. Same numbers,
// completely different fights.
//
// ── WHAT THE NPC ACTUALLY DOES ───────────────────────────────
// It runs a REAL TiltEngine on a REAL table. It is not faking a
// score curve. It flips, it drains, it tilts, it scores. It is
// playing the same game you are.
//
// It does TWO things per frame:
//   1. FLIP — decide whether to press each flipper, by predicting
//      whether the ball will reach the flipper's strike zone.
//   2. SCHEME — decide whether to spend charge on scoring or on
//      RUINING YOUR DAY.
//
// ── THE FLIPPER BRAIN ────────────────────────────────────────
// A perfect flipper AI is trivial (flip when ball enters zone)
// and it is UNBEATABLE and NO FUN. So the AI is deliberately
// imperfect in a HUMAN way: it has a REACTION DELAY and a
// TIMING JITTER, both scaled by skill. A low-skill NPC flips
// late and mistimed — exactly like a person who's tired.
//
// The AI never cheats: it reads the same ball position you see,
// with the same physics, and it can lose. It must be able to
// lose, or beating it means nothing.
// ============================================================

import { getPower, POWERS } from "./Powers";
import { len } from "./physics/Vec";
import { PHASE } from "./TiltEngine";

// ── TEMPERAMENTS ──────────────────────────────────────────────
// Each is a full personality. `aggression` is the sabotage
// appetite; `greed` is how long it hoards charge before spending;
// `spite` is how much it prefers hurting you over helping itself.
export const TEMPERAMENTS = {
  // Plays its own game. Barely notices you exist. The baseline.
  stoic: {
    id: "stoic", name: "Stoic", glyph: "◇",
    aggression: 0.15, greed: 0.7, spite: 0.1,
    taunts: ["...", "Hm.", "Continue."],
  },
  // Sabotages constantly, badly, on cooldown. Chaos, not strategy.
  rabid: {
    id: "rabid", name: "Rabid", glyph: "☠",
    aggression: 0.9, greed: 0.1, spite: 0.85,
    taunts: ["HA!", "Drain. DRAIN.", "Not today.", "Choke on it."],
  },
  // Hoards charge, then dumps three sabotages the moment you light
  // a mode. The worst one to play against. The best one to beat.
  patient: {
    id: "patient", name: "Patient", glyph: "◈",
    aggression: 0.55, greed: 0.9, spite: 0.6,
    taunts: ["I was waiting.", "There it is.", "Now."],
  },
  // Only sabotages when LOSING. Perfectly fair until it isn't.
  vindictive: {
    id: "vindictive", name: "Vindictive", glyph: "✖",
    aggression: 0.4, greed: 0.5, spite: 0.95,
    taunts: ["You're ahead.", "Were.", "That evens it."],
  },
  // Never sabotages. Just plays BETTER than you. Terrifying.
  pure: {
    id: "pure", name: "Pure", glyph: "○",
    aggression: 0.0, greed: 0.4, spite: 0.0,
    taunts: ["Watch.", "Again.", "Like that."],
  },
};

export const TEMPERAMENT_LIST = Object.values(TEMPERAMENTS);

// ── SKILL BANDS ───────────────────────────────────────────────
// skill ∈ [0,1]. Everything about competence derives from this.
// One number, one source of truth — no scattered difficulty flags.
export const skillProfile = (skill) => {
  const s = Math.max(0, Math.min(1, skill));
  return {
    skill: s,
    // Reaction: 240ms (novice) → 55ms (machine). A human pro is ~150ms.
    reactionMs: 240 - s * 185,
    // Timing jitter on the flip — the difference between a clean
    // ramp shot and a dribble down the outlane.
    jitter: 0.42 - s * 0.38,
    // How reliably it recognizes a scoring opportunity.
    aimError: 0.35 - s * 0.30,
    // How well it aims a SABOTAGE. A low-skill NPC anchors nowhere
    // useful. A high-skill one finds your ramp mouth every time.
    sabotageAim: s,
    // How much it plans ahead vs reacts.
    lookahead: 0.05 + s * 0.28,   // seconds of ball-path prediction
    label: s < 0.25 ? "Novice" : s < 0.5 ? "Steady"
         : s < 0.72 ? "Sharp"  : s < 0.9 ? "Vicious" : "Machine",
  };
};

// ── THE NPC ───────────────────────────────────────────────────
export class TiltNPC {
  /**
   * @param engine     the NPC's OWN TiltEngine (it plays for real)
   * @param opts.skill 0..1
   * @param opts.temperament  key of TEMPERAMENTS
   * @param opts.onSabotage   (powerId, point) => void — the outbound hook.
   *                          The NPC does NOT know about the SabotageBus.
   *                          It just declares intent; the duel driver wires
   *                          it to a transport. Same separation as Terror's
   *                          NPC ↔ ResponseBridge: zero circular imports.
   */
  constructor(engine, opts = {}) {
    this.engine = engine;
    this.prof = skillProfile(opts.skill ?? 0.5);
    this.temp = TEMPERAMENTS[opts.temperament] || TEMPERAMENTS.stoic;
    this.onSabotage = opts.onSabotage || (() => {});
    this.onTaunt = opts.onTaunt || (() => {});

    this.name = opts.name || `${this.temp.name} ${this.prof.label}`;

    // Flip state
    this.pendL = 0;      // reaction countdown for left flipper
    this.pendR = 0;
    this.holdL = 0;      // how long to hold the flip
    this.holdR = 0;

    // Scheme state
    this.thinkTimer = 0;
    this.plungerHold = 0;
    this.tauntTimer = 0;
    this.lastSabotage = 0;

    // The NPC's read of YOUR table. The duel driver feeds this in
    // every frame. It is the ONLY thing the NPC knows about you —
    // it is not reading your engine, it is reading a SNAPSHOT.
    // (Same discipline as the render layer. Nothing outside the
    // engine gets a live reference into physics.)
    this.enemy = null;
  }

  /** The duel driver calls this each frame with the player's snapshot. */
  observe(enemySnapshot) { this.enemy = enemySnapshot; }

  // ── THE FRAME ───────────────────────────────────────────────
  tick(dt) {
    const e = this.engine;

    // Auto-plunge. It waits a beat — an instant launch reads as a bot.
    if (e.phase === PHASE.READY) {
      this.plungerHold += dt;
      e.pullPlunger(dt);
      if (this.plungerHold > 0.6 + (1 - this.prof.skill) * 0.8) {
        e.release();
        this.plungerHold = 0;
      }
      return;
    }
    if (e.phase !== PHASE.LIVE) { this._reset(); return; }

    this._flip(dt);
    this._scheme(dt);

    if (this.tauntTimer > 0) this.tauntTimer -= dt;
  }

  _reset() {
    this.pendL = this.pendR = 0;
    this.holdL = this.holdR = 0;
    this.engine.press("L", false);
    this.engine.press("R", false);
  }

  // ── FLIPPER BRAIN ───────────────────────────────────────────
  // Predict the ball forward `lookahead` seconds under gravity. If
  // the predicted path enters a flipper's strike zone, arm a flip
  // — after a REACTION DELAY. That delay is the entire difficulty
  // curve, and it's honest: the AI isn't given worse physics, it's
  // given slower nerves.
  _flip(dt) {
    const e = this.engine;
    const ball = e.primary();

    // Countdown any armed flips
    if (this.pendL > 0) {
      this.pendL -= dt;
      if (this.pendL <= 0) { e.press("L", true); this.holdL = 0.16; }
    }
    if (this.pendR > 0) {
      this.pendR -= dt;
      if (this.pendR <= 0) { e.press("R", true); this.holdR = 0.16; }
    }
    // Release held flips
    if (this.holdL > 0) {
      this.holdL -= dt;
      if (this.holdL <= 0) e.press("L", false);
    }
    if (this.holdR > 0) {
      this.holdR -= dt;
      if (this.holdR <= 0) e.press("R", false);
    }

    if (!ball || ball.frozen > 0) return;

    // Only think about balls that are FALLING toward the flippers.
    if (ball.vel.y < 20) return;

    for (const f of e.flippers) {
      const side = f.side;
      const armed = side === "L" ? this.pendL > 0 || this.holdL > 0
                                 : this.pendR > 0 || this.holdR > 0;
      if (armed) continue;

      // Where will the ball be when it reaches the flipper's Y?
      const dy = f.pivot.y - ball.p.y;
      if (dy <= 0) continue;                       // already past
      const g = e.world.gravity * ball.gravityMul;
      // t from  dy = vy*t + ½gt²   →  quadratic
      const disc = ball.vel.y * ball.vel.y + 2 * g * dy;
      if (disc < 0) continue;
      const t = (-ball.vel.y + Math.sqrt(disc)) / g;
      if (t < 0 || t > this.prof.lookahead + 0.5) continue;

      const px = ball.p.x + ball.vel.x * t;

      // Is the predicted landing inside this flipper's reach?
      const reachMin = side === "L" ? f.pivot.x - 8 : f.pivot.x - f.length - 8;
      const reachMax = side === "L" ? f.pivot.x + f.length + 8 : f.pivot.x + 8;
      if (px < reachMin || px > reachMax) continue;

      // AIM ERROR — a bad NPC misjudges where the ball will land and
      // flips at the wrong moment. This is the honest way to make an
      // AI worse: degrade its PREDICTION, never its physics.
      const err = (this.engine.world.rng() - 0.5) * 2 * this.prof.aimError * 40;
      const pxErr = px + err;
      if (pxErr < reachMin - 20 || pxErr > reachMax + 20) continue;

      // Arm the flip so it fires just as the ball arrives, plus
      // reaction delay, plus jitter.
      const jit = (this.engine.world.rng() - 0.5) * this.prof.jitter * 0.28;
      const delay = Math.max(0, t - 0.06) + (this.prof.reactionMs / 1000) * 0.5 + jit;

      if (side === "L") this.pendL = delay;
      else              this.pendR = delay;
    }
  }

  // ── THE SCHEMER ─────────────────────────────────────────────
  // Every ~0.5s the NPC asks one question:
  //   "Do I spend this charge on ME, or on THEM?"
  //
  // temperament.aggression sets the base appetite.
  // temperament.greed sets the charge threshold it waits for.
  // temperament.spite biases the answer when it's LOSING.
  _scheme(dt) {
    const e = this.engine;
    this.thinkTimer -= dt;
    this.lastSabotage += dt;
    if (this.thinkTimer > 0) return;
    this.thinkTimer = 0.45;

    const rng = e.world.rng;
    const t = this.temp;

    // GREED GATE — a greedy NPC hoards. It won't spend below its
    // threshold, which is why "patient" feels like it's building to
    // something. It IS.
    const threshold = 25 + t.greed * 55;
    if (e.charge < threshold) return;

    // Am I losing? (Only meaningful if we can see the enemy.)
    const myScore  = e.score;
    const theirScore = this.enemy?.score ?? 0;
    const losing = theirScore > myScore * 1.15;
    const margin = theirScore > 0 ? myScore / theirScore : 1;

    // The appetite roll. Spite AMPLIFIES aggression when behind —
    // that's what makes "vindictive" work: it's a lamb until you're
    // ahead, then it's a wolf.
    let appetite = t.aggression;
    if (losing) appetite += t.spite * 0.55;
    // And it swings HARD when the enemy is mid-mode (about to score
    // big). A high-skill NPC recognizes the moment; a low-skill one
    // doesn't notice.
    const enemyHot = this.enemy?.combo >= 4 || this.enemy?.multiplier > 1;
    if (enemyHot) appetite += this.prof.skill * 0.4;

    const wantSabotage = this.enemy && rng() < appetite;

    if (wantSabotage && this.lastSabotage > 1.4) {
      this._sabotage();
      return;
    }

    // Otherwise: spend on itself. A "pure" NPC lives here forever.
    this._selfCast();
  }

  _sabotage() {
    const e = this.engine;
    const rng = e.world.rng;

    // Pick from the sabotage kit it can afford.
    const kit = this.engine.loadout
      .map(getPower)
      .filter((p) => p && p.sabotage && e.charge >= p.cost);
    if (!kit.length) return;
    const p = kit[Math.floor(rng() * kit.length)];

    let point = null;
    if (p.target === "point") point = this._aimSabotage(p);

    // Spend and declare. The engine's own cast() would apply it to
    // OUR table — so we debit manually and fire the outbound hook.
    // (The duel driver routes it to the SabotageBus.)
    e.charge = Math.max(0, e.charge - p.cost);
    this.lastSabotage = 0;
    this.onSabotage(p.id, point);
    this._taunt();
  }

  /**
   * WHERE to put a hostile Anchor/Magnet. This is where NPC skill
   * shows most vividly, and it's the most fun part of the AI:
   *
   *   LOW SKILL  — throws it at the ball's current position.
   *                Harmless. Sometimes accidentally brilliant.
   *   MID SKILL  — throws it in the ball's PATH. Actually annoying.
   *   HIGH SKILL — throws it at the enemy's RAMP MOUTH, or their
   *                OUTLANE. It has read the table. It is hunting.
   */
  _aimSabotage(power) {
    const rng = this.engine.world.rng;
    const en = this.enemy;
    if (!en || !en.balls?.length) return null;
    const b = en.balls[0];
    const s = this.prof.sabotageAim;

    // Low skill: right on top of the ball. Useless-ish.
    if (s < 0.35 || rng() > s) {
      return { x: b.x + (rng() - 0.5) * 60, y: b.y + (rng() - 0.5) * 60 };
    }

    // Mid skill: lead the ball. Put it where the ball is GOING.
    if (s < 0.7) {
      const lead = 0.28;
      return {
        x: b.x + b.vx * lead + (rng() - 0.5) * 30,
        y: b.y + b.vy * lead + (rng() - 0.5) * 30,
      };
    }

    // HIGH SKILL: hunt the enemy's infrastructure.
    // Magnetize → their OUTLANE (drag the ball to its death).
    // Anchor    → their RAMP MOUTH (kill their economy).
    const W = en.W ?? 400, H = en.H ?? 700;
    if (power.id === "magnetize") {
      // Whichever outlane the ball is nearer. Cruelty with geometry.
      const leftSide = b.x < W / 2;
      return { x: leftSide ? 34 : W - 34, y: H - 130 };
    }
    if (power.id === "anchor") {
      // Anchor across the mouth of the big ramp — right side, above
      // the flipper. (Generic guess that's correct on Spite, Foundry,
      // and most tables, because that's where ramps go.)
      return { x: W - 104, y: H - 200 };
    }
    if (power.id === "reverse") return null;      // no aim needed
    return { x: b.x, y: b.y };
  }

  _selfCast() {
    const e = this.engine;
    const rng = e.world.rng;
    const ball = e.primary();
    if (!ball) return;

    // Panic Freeze — the ball is about to drain and the NPC knows it.
    // A skilled NPC saves itself; a novice doesn't see it coming.
    const draining = ball.p.y > e.table.H - 190 && ball.vel.y > 90;
    if (draining && rng() < this.prof.skill * 0.7) {
      if (e.canCast("freeze")) { e.cast("freeze"); return; }
      if (e.canCast("magnetize")) {
        // Magnetize itself back UP the table. Desperate. Effective.
        e.cast("magnetize", { x: e.table.W / 2, y: 260 });
        return;
      }
    }

    // Greed: Overclock when safe and scoring.
    if (!draining && e.combo >= 3 && rng() < 0.5) {
      if (e.canCast("overclock")) { e.cast("overclock"); return; }
    }
  }

  _taunt() {
    if (this.tauntTimer > 0) return;
    const rng = this.engine.world.rng;
    if (rng() > 0.5) return;      // it doesn't ALWAYS have something to say
    const line = this.temp.taunts[Math.floor(rng() * this.temp.taunts.length)];
    this.tauntTimer = 3.5;
    this.onTaunt(line);
  }
}

// ── Roster — the single-player Gauntlet ladder ────────────────
// skill climbs, temperament rotates. The rotation is the point:
// beating a Rabid teaches you nothing about beating a Patient.
export const GAUNTLET = [
  { id: "npc_1", name: "Slug",     skill: 0.18, temperament: "stoic",
    blurb: "It barely knows you're there." },
  { id: "npc_2", name: "Yap",      skill: 0.32, temperament: "rabid",
    blurb: "Sabotages constantly. Badly." },
  { id: "npc_3", name: "Hush",     skill: 0.48, temperament: "pure",
    blurb: "Won't touch you. Doesn't need to." },
  { id: "npc_4", name: "Grudge",   skill: 0.58, temperament: "vindictive",
    blurb: "Fair until you're winning." },
  { id: "npc_5", name: "The Wait", skill: 0.70, temperament: "patient",
    blurb: "It is saving up. For you." },
  { id: "npc_6", name: "Maw",      skill: 0.82, temperament: "rabid",
    blurb: "Fast AND cruel. Bring Freeze." },
  { id: "npc_7", name: "V'ael",    skill: 0.94, temperament: "patient",
    blurb: "She has read the table. She has read you." },
];

export default TiltNPC;
